// Phase 16 — symbol → image asset helpers (shared by single + bulk export).
//
// BROWSER-ONLY rendering: the glyph is drawn with the PAGE's loaded fonts, so
// self-hosted symbol/math glyphs (STIX, Noto Sans Symbols 2, …) AND colour emoji
// export accurately — not as the "No Glyph" boxes the old SVG-in-<img> pipeline
// produced (an <img>-loaded SVG can't use the page's @font-face web fonts).

import { allSymbols } from "@/data/symbol-resolve";
import { getSymbolName } from "@/data/symbol-names";

export interface AssetItem {
  symbol: string;
  slug: string;
  name: string;
}

export type ExportFormat = "svg" | "png" | "both";

// The page's real glyph stack: self-hosted symbol/math families first (so
// web-font-only glyphs render), then the device's COLOUR emoji font (so colour
// emoji keep their colours), then system symbol fallbacks.
export const EXPORT_FONT_STACK =
  '"STIX Two Math","Noto Sans Symbols 2","Noto Sans","Noto Sans Mono","Noto Sans JP","Noto Sans KR","Noto Music","Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","Segoe UI Symbol","Apple Symbols",sans-serif';

// Self-hosted families that use unicode-range + font-display:swap, so a glyph not
// yet painted on the page may not be downloaded yet — load it before we rasterise.
const SELF_HOSTED_FAMILIES = [
  "STIX Two Math", "Noto Sans Symbols 2", "Noto Sans", "Noto Sans Mono",
  "Noto Sans JP", "Noto Sans KR", "Noto Music",
];

export function escapeXml(s: string): string {
  return s.replace(
    /[<>&'"]/g,
    (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" })[c]!,
  );
}

const GLYPH_FONT_PX = (size: number, hasBg = false) => Math.round(size * (hasBg ? 0.56 : 0.72));

// --- background shape (icons / avatars) ------------------------------------
export type BgShape = "none" | "circle" | "rounded" | "square";
export interface Background {
  shape: BgShape;
  color: string;
}
export const NO_BG: Background = { shape: "none", color: "#000000" };

function roundRectPath(ctx: CanvasRenderingContext2D, w: number, h: number, r: number) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(w, 0, w, h, r);
  ctx.arcTo(w, h, 0, h, r);
  ctx.arcTo(0, h, 0, 0, r);
  ctx.arcTo(0, 0, w, 0, r);
  ctx.closePath();
}

/** Paint the background shape onto a canvas (no-op for "none"). */
function paintBackground(ctx: CanvasRenderingContext2D, w: number, h: number, bg: Background) {
  if (bg.shape === "none") return;
  ctx.save();
  ctx.fillStyle = bg.color;
  if (bg.shape === "circle") {
    if (w === h) {
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      roundRectPath(ctx, w, h, h / 2); // pill for non-square (inline) lockups
      ctx.fill();
    }
  } else if (bg.shape === "rounded") {
    roundRectPath(ctx, w, h, Math.min(w, h) * 0.22);
    ctx.fill();
  } else {
    ctx.fillRect(0, 0, w, h);
  }
  ctx.restore();
}

/** Background shape as an SVG element string (for the vector single-glyph SVG). */
function bgSvg(bg: Background, w: number, h: number): string {
  if (bg.shape === "none") return "";
  const c = escapeXml(bg.color);
  if (bg.shape === "circle") {
    if (w === h) return `<circle cx="${w / 2}" cy="${h / 2}" r="${Math.min(w, h) / 2}" fill="${c}"/>`;
    const r = h / 2;
    return `<rect width="${w}" height="${h}" rx="${r}" ry="${r}" fill="${c}"/>`;
  }
  if (bg.shape === "rounded") {
    const r = Math.min(w, h) * 0.22;
    return `<rect width="${w}" height="${h}" rx="${r}" ry="${r}" fill="${c}"/>`;
  }
  return `<rect width="${w}" height="${h}" fill="${c}"/>`;
}

// Size a canvas so the background shape fully CONTAINS tiled content (a pattern or
// a multi-symbol grid). Without this, a circle inscribed in a square pattern clips
// the corner symbols. Returns the padded canvas size + the content's offset.
//   - circle, roughly-square content → a circle that circumscribes the content
//   - circle, wide/tall content      → a pill (margin box; paintBackground rounds it)
//   - rounded / square               → the content box plus a margin
function backgroundLayout(
  cw: number,
  ch: number,
  bg: Background,
  cell: number,
): { W: number; H: number; offX: number; offY: number } {
  if (bg.shape === "none") return { W: cw, H: ch, offX: 0, offY: 0 };
  const pad = Math.round(cell * 0.45);
  const ratio = cw / ch;
  if (bg.shape === "circle" && ratio >= 0.6 && ratio <= 1.7) {
    const d = Math.ceil(Math.sqrt(cw * cw + ch * ch)) + pad * 2;
    return { W: d, H: d, offX: Math.round((d - cw) / 2), offY: Math.round((d - ch) / 2) };
  }
  return { W: cw + pad * 2, H: ch + pad * 2, offX: pad, offY: pad };
}

// --- font readiness --------------------------------------------------------
async function ensureFontsLoaded(symbol: string, px: number): Promise<void> {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts) return;
  try {
    await Promise.all(
      SELF_HOSTED_FAMILIES.map((f) => fonts.load(`${px}px "${f}"`, symbol).catch(() => {})),
    );
    await fonts.ready;
  } catch {
    /* best-effort: fall back to whatever's already loaded */
  }
}

// --- canvas raster ---------------------------------------------------------
/** Draw a symbol centred on a transparent canvas using the page's real fonts.
 *  `color` fills monochrome glyphs; colour emoji ignore it and keep their own. */
export async function renderSymbolCanvas(
  symbol: string,
  size = 512,
  color = "#111111",
  bg: Background = NO_BG,
): Promise<HTMLCanvasElement> {
  const px = GLYPH_FONT_PX(size, bg.shape !== "none");
  await ensureFontsLoaded(symbol, px);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.clearRect(0, 0, size, size);
  paintBackground(ctx, size, size, bg);
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `${px}px ${EXPORT_FONT_STACK}`;
  ctx.fillText(symbol, size / 2, size / 2);
  return canvas;
}

/** Rasterise a symbol to a PNG Blob (transparent unless a background is set). */
export async function symbolToPng(
  symbol: string,
  size = 512,
  color = "#111111",
  bg: Background = NO_BG,
): Promise<Blob> {
  const canvas = await renderSymbolCanvas(symbol, size, color, bg);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/png"),
  );
}

// --- @font-face discovery (to embed the exact glyph font into the SVG) ------
interface FaceInfo {
  family: string;
  url: string;
  ranges: [number, number][];
}
let faceCache: FaceInfo[] | null = null;

function parseRanges(s: string): [number, number][] {
  const out: [number, number][] = [];
  if (!s) return out; // no unicode-range → unreliable as glyph coverage; skip the face
  for (const part of s.split(",").map((x) => x.trim())) {
    let m: RegExpMatchArray | null;
    if ((m = part.match(/^U\+([0-9A-Fa-f]+)-([0-9A-Fa-f]+)$/))) {
      out.push([parseInt(m[1], 16), parseInt(m[2], 16)]);
    } else if ((m = part.match(/^U\+([0-9A-Fa-f?]+)$/))) {
      const hex = m[1];
      if (hex.includes("?")) {
        out.push([parseInt(hex.replace(/\?/g, "0"), 16), parseInt(hex.replace(/\?/g, "F"), 16)]);
      } else {
        const c = parseInt(hex, 16);
        out.push([c, c]);
      }
    }
  }
  return out;
}

function collectFaces(): FaceInfo[] {
  if (faceCache) return faceCache;
  const out: FaceInfo[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList | null = null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin sheet — skip
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      const style = rule.style;
      const family = (style.getPropertyValue("font-family") || "").replace(/^["']|["']$/g, "").trim();
      const ranges = parseRanges(style.getPropertyValue("unicode-range") || "");
      if (!family || ranges.length === 0) continue; // rangeless text fonts (Inter…) aren't glyph providers
      const m = (style.getPropertyValue("src") || "").match(/url\(\s*["']?([^"')]+\.woff2)["']?\s*\)/i);
      if (!m) continue;
      out.push({ family, url: m[1], ranges });
    }
  }
  faceCache = out;
  return out;
}

/** A self-hosted MONOCHROME face that covers every codepoint of `symbol`, or null
 *  (emoji families are excluded — colour emoji are exported as a raster instead). */
function findMonoFace(symbol: string): FaceInfo | null {
  const points = Array.from(symbol).map((c) => c.codePointAt(0)!);
  for (const f of collectFaces()) {
    if (/emoji/i.test(f.family)) continue;
    if (points.every((cp) => f.ranges.some(([a, b]) => cp >= a && cp <= b))) return f;
  }
  return null;
}

const woff2Cache = new Map<string, string>();
async function fetchWoff2Base64(url: string): Promise<string> {
  const cached = woff2Cache.get(url);
  if (cached) return cached;
  const buf = await (await fetch(url)).arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  const b64 = btoa(bin);
  woff2Cache.set(url, b64);
  return b64;
}

// --- SVG -------------------------------------------------------------------
/**
 * Self-contained SVG. Monochrome glyphs → a vector `<text>` with the covering
 * self-hosted font EMBEDDED (so it stays scalable + recolourable and renders in
 * any viewer). Colour emoji (or any glyph with no monochrome face) → a high-res
 * raster `<image>` drawn with the page fonts, so colours are preserved. Both are
 * fully portable — no dependence on the viewer's installed fonts.
 */
export async function symbolToSvg(
  symbol: string,
  size = 512,
  color = "#111111",
  bg: Background = NO_BG,
): Promise<string> {
  const face = typeof document !== "undefined" ? findMonoFace(symbol) : null;
  if (face) {
    try {
      const b64 = await fetchWoff2Base64(face.url);
      return [
        `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="${escapeXml(symbol)}">`,
        `<defs><style>@font-face{font-family:"ExportGlyph";src:url(data:font/woff2;base64,${b64}) format("woff2");}</style></defs>`,
        bgSvg(bg, size, size),
        `<text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" font-size="${GLYPH_FONT_PX(size, bg.shape !== "none")}" font-family="ExportGlyph,${escapeXml(EXPORT_FONT_STACK)}" fill="${escapeXml(color)}">${escapeXml(symbol)}</text>`,
        `</svg>`,
      ].join("");
    } catch {
      /* fall through to raster on fetch/encode failure */
    }
  }
  // Raster fallback: colour emoji, multi-font clusters, or a failed font fetch.
  const canvas = await renderSymbolCanvas(symbol, Math.max(size, 1024), color, bg);
  const href = canvas.toDataURL("image/png");
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" role="img" aria-label="${escapeXml(symbol)}">`,
    `<image x="0" y="0" width="${size}" height="${size}" href="${href}"/>`,
    `</svg>`,
  ].join("");
}

/** True when the glyph is exported as a scalable vector (has a monochrome face);
 *  false when it falls back to an embedded raster (e.g. colour emoji). */
export function isVectorExportable(symbol: string): boolean {
  return typeof document !== "undefined" && findMonoFace(symbol) !== null;
}

// --- composition: symbol + user text --------------------------------------
// Turns a symbol into a captioned icon / badge or a symbol-beside-text lockup,
// so a user can make a quick label, avatar or sticker. Rendered on canvas with
// the page's real fonts (symbol glyph + a clean display font for the text).

export type CompositionLayout = "stack" | "before" | "after" | "around";

export interface CompositionOpts {
  symbol: string;
  text: string;
  color?: string;
  layout?: CompositionLayout;
  /** Square side for "stack"; line height for the inline layouts. */
  size?: number;
  bg?: Background;
}

// Caption stack: a display font for plain letters first, then the self-hosted
// symbol/math families so FANCY-FONT captions (Mathematical-Alphanumeric, circled,
// full-width, fraktur, … from the font generator) render too, then colour emoji.
const TEXT_FONT_STACK =
  '"Space Grotesk","Inter","STIX Two Math","Noto Sans Symbols 2","Noto Sans","Noto Sans Mono","Noto Sans JP","Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';

const CAPTION_FAMILIES = [
  "Space Grotesk", "Inter", "STIX Two Math", "Noto Sans Symbols 2", "Noto Sans", "Noto Sans Mono", "Noto Sans JP",
];

async function ensureTextFont(textPx: number, text: string): Promise<void> {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts) return;
  await Promise.all(
    CAPTION_FAMILIES.map((f) =>
      fonts.load(`${f === "Space Grotesk" ? "700 " : ""}${textPx}px "${f}"`, text).catch(() => {}),
    ),
  );
}

/** Render a symbol + text composition to a canvas (transparent background). */
export async function renderCompositionCanvas(opts: CompositionOpts): Promise<HTMLCanvasElement> {
  const { symbol, color = "#111111", layout = "stack", size = 512, bg = NO_BG } = opts;
  const text = opts.text.trim();
  if (!text) return renderSymbolCanvas(symbol, size, color, bg);
  const hasBg = bg.shape !== "none";

  const draw = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context");
    return ctx;
  };

  if (layout === "stack") {
    // Captioned icon: symbol on top, text below, in a square.
    const S = size;
    const symPx = Math.round(S * 0.46);
    let textPx = Math.round(S * 0.15);
    await ensureFontsLoaded(symbol, symPx);
    await ensureTextFont(textPx, text);
    const canvas = document.createElement("canvas");
    canvas.width = S;
    canvas.height = S;
    const ctx = draw(canvas);
    // Shrink the caption until it fits the width (tighter inside a background so
    // it stays within a circle / rounded card).
    const maxW = S * (hasBg ? 0.72 : 0.88);
    ctx.font = `700 ${textPx}px ${TEXT_FONT_STACK}`;
    while (textPx > 8 && ctx.measureText(text).width > maxW) {
      textPx -= 2;
      ctx.font = `700 ${textPx}px ${TEXT_FONT_STACK}`;
    }
    ctx.clearRect(0, 0, S, S);
    paintBackground(ctx, S, S, bg);
    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${symPx}px ${EXPORT_FONT_STACK}`;
    ctx.fillText(symbol, S / 2, hasBg ? S * 0.41 : S * 0.4);
    ctx.font = `700 ${textPx}px ${TEXT_FONT_STACK}`;
    ctx.fillText(text, S / 2, hasBg ? S * 0.72 : S * 0.78);
    return canvas;
  }

  // Inline lockups: symbol before / after / around the text on one line.
  const H = size;
  const symPx = Math.round(H * 0.6);
  const textPx = Math.round(H * 0.34);
  await ensureFontsLoaded(symbol, symPx);
  await ensureTextFont(textPx, text);
  const measure = document.createElement("canvas").getContext("2d")!;
  measure.font = `${symPx}px ${EXPORT_FONT_STACK}`;
  const symW = measure.measureText(symbol).width;
  measure.font = `700 ${textPx}px ${TEXT_FONT_STACK}`;
  const textW = measure.measureText(text).width;
  const gap = Math.round(H * 0.16);
  const padX = Math.round(H * 0.2);
  const both = layout === "around";
  const W = Math.ceil(padX * 2 + textW + gap + symW + (both ? gap + symW : 0));

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = draw(canvas);
  ctx.clearRect(0, 0, W, H);
  paintBackground(ctx, W, H, bg);
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  const y = H / 2;
  let x = padX;
  const sym = () => {
    ctx.font = `${symPx}px ${EXPORT_FONT_STACK}`;
    ctx.fillText(symbol, x, y);
    x += symW + gap;
  };
  const label = () => {
    ctx.font = `700 ${textPx}px ${TEXT_FONT_STACK}`;
    ctx.fillText(text, x, y);
    x += textW + gap;
  };
  if (layout === "before" || both) sym();
  label();
  if (layout === "after" || both) sym();
  return canvas;
}

/** Composition → PNG Blob. */
export async function compositionToPng(opts: CompositionOpts): Promise<Blob> {
  const canvas = await renderCompositionCanvas(opts);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png"),
  );
}

/** Composition → self-contained SVG (high-res raster embedded as <image>). */
export async function compositionToSvg(opts: CompositionOpts): Promise<string> {
  const hi = Math.max(opts.size ?? 512, 1024);
  const canvas = await renderCompositionCanvas({ ...opts, size: hi });
  const href = canvas.toDataURL("image/png");
  const w = canvas.width;
  const h = canvas.height;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${escapeXml(`${opts.symbol} ${opts.text}`)}">`,
    `<image x="0" y="0" width="${w}" height="${h}" href="${href}"/>`,
    `</svg>`,
  ].join("");
}

// --- patterns: repeat / grid / border -------------------------------------
// Tile a symbol into a divider (row), a grid (pattern tile) or a frame (border).
export type PatternType = "row" | "grid" | "border";

export interface PatternOpts {
  symbol: string;
  type: PatternType;
  /** Repeats per row. */
  count?: number;
  /** Rows (grid / border). */
  rows?: number;
  color?: string;
  bg?: Background;
  /** Per-glyph cell size in px. */
  cell?: number;
}

const clampInt = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.round(n)));

function patternDims(opts: PatternOpts): { cols: number; rows: number } {
  const cols = clampInt(opts.count ?? (opts.type === "row" ? 7 : opts.type === "border" ? 8 : 6), 1, 40);
  const rows = opts.type === "row" ? 1 : clampInt(opts.rows ?? 3, opts.type === "border" ? 2 : 1, 20);
  return { cols, rows };
}

/** A copy-pasteable plain-text version of the pattern (the site's copy ethos). */
export function patternText(opts: PatternOpts): string {
  const { symbol } = opts;
  const { cols, rows } = patternDims(opts);
  if (opts.type === "row") return symbol.repeat(cols);
  if (opts.type === "grid") return Array.from({ length: rows }, () => symbol.repeat(cols)).join("\n");
  // border
  const edge = symbol.repeat(cols);
  if (rows <= 2) return [edge, edge].slice(0, rows).join("\n");
  const mid = symbol + " ".repeat(Math.max(1, cols - 2)) + symbol;
  return [edge, ...Array.from({ length: rows - 2 }, () => mid), edge].join("\n");
}

/** Render a pattern to a canvas (transparent unless a background is set). */
export async function renderPatternCanvas(opts: PatternOpts): Promise<HTMLCanvasElement> {
  const { symbol, type, color = "#111111", bg = NO_BG } = opts;
  const cell = opts.cell ?? 160;
  const glyphPx = Math.round(cell * 0.78);
  const { cols, rows } = patternDims(opts);
  await ensureFontsLoaded(symbol, glyphPx);
  const cw = cols * cell;
  const ch = rows * cell;
  // Size the canvas so a background shape contains the whole pattern (a circle
  // circumscribes it instead of clipping the corner symbols).
  const { W, H, offX, offY } = backgroundLayout(cw, ch, bg, cell);
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.clearRect(0, 0, W, H);
  paintBackground(ctx, W, H, bg);
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `${glyphPx}px ${EXPORT_FONT_STACK}`;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (type === "border" && r !== 0 && r !== rows - 1 && c !== 0 && c !== cols - 1) continue;
      ctx.fillText(symbol, offX + c * cell + cell / 2, offY + r * cell + cell / 2);
    }
  }
  return canvas;
}

export async function patternToPng(opts: PatternOpts): Promise<Blob> {
  const canvas = await renderPatternCanvas(opts);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png"),
  );
}

export async function patternToSvg(opts: PatternOpts): Promise<string> {
  const canvas = await renderPatternCanvas(opts);
  const href = canvas.toDataURL("image/png");
  const w = canvas.width;
  const h = canvas.height;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${escapeXml(`${opts.symbol} ${opts.type}`)}">`,
    `<image x="0" y="0" width="${w}" height="${h}" href="${href}"/>`,
    `</svg>`,
  ].join("");
}

// --- multi-symbol: compose several symbols into one image ------------------
export interface MultiOpts {
  symbols: string[];
  /** Columns; 0/undefined = a single row. */
  columns?: number;
  color?: string;
  bg?: Background;
  cell?: number;
}

export async function renderMultiCanvas(opts: MultiOpts): Promise<HTMLCanvasElement> {
  const { symbols, color = "#111111", bg = NO_BG } = opts;
  const cell = opts.cell ?? 160;
  const glyphPx = Math.round(cell * 0.72);
  const n = Math.max(1, symbols.length);
  const cols = clampInt(opts.columns && opts.columns > 0 ? opts.columns : n, 1, 40);
  const rows = Math.ceil(n / cols);
  await ensureFontsLoaded(symbols.join(""), glyphPx);
  const cw = cols * cell;
  const ch = rows * cell;
  const { W, H, offX, offY } = backgroundLayout(cw, ch, bg, cell);
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.clearRect(0, 0, W, H);
  paintBackground(ctx, W, H, bg);
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `${glyphPx}px ${EXPORT_FONT_STACK}`;
  symbols.forEach((sym, i) => {
    const r = Math.floor(i / cols);
    const c = i % cols;
    ctx.fillText(sym, offX + c * cell + cell / 2, offY + r * cell + cell / 2);
  });
  return canvas;
}

export async function multiToPng(opts: MultiOpts): Promise<Blob> {
  const canvas = await renderMultiCanvas(opts);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/png"),
  );
}

export async function multiToSvg(opts: MultiOpts): Promise<string> {
  const canvas = await renderMultiCanvas(opts);
  const href = canvas.toDataURL("image/png");
  const w = canvas.width;
  const h = canvas.height;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${escapeXml(opts.symbols.join(" "))}">`,
    `<image x="0" y="0" width="${w}" height="${h}" href="${href}"/>`,
    `</svg>`,
  ].join("");
}

/** Filenames are derived from the slug so a ZIP is human-navigable. */
export function assetFilename(slug: string, ext: "svg" | "png"): string {
  return `${slug}.${ext}`;
}

// --- symbol → catalogue entry resolution -----------------------------------
let bySymbol: Map<string, AssetItem> | null = null;
function lookup(): Map<string, AssetItem> {
  if (bySymbol) return bySymbol;
  bySymbol = new Map();
  for (const s of allSymbols) {
    if (!bySymbol.has(s.symbol)) {
      bySymbol.set(s.symbol, { symbol: s.symbol, slug: s.slug, name: getSymbolName(s.symbol) });
    }
  }
  return bySymbol;
}

function fallbackSlug(symbol: string): string {
  return (
    "u" +
    Array.from(symbol)
      .map((c) => c.codePointAt(0)!.toString(16).padStart(4, "0"))
      .join("-")
  );
}

/** Resolve raw symbol strings to export items, de-duplicated by slug. */
export function resolveAssets(symbols: string[]): AssetItem[] {
  const map = lookup();
  const seen = new Set<string>();
  const out: AssetItem[] = [];
  for (const symbol of symbols) {
    const item = map.get(symbol) ?? { symbol, slug: fallbackSlug(symbol), name: symbol };
    if (seen.has(item.slug)) continue;
    seen.add(item.slug);
    out.push(item);
  }
  return out;
}
